import React, { useCallback, useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import PaginationAgent from "components/pagination/PaginationAgent";
import { CompPagination, Container } from "components";
import { AiOutlinePlus } from "react-icons/ai";
import { LuEdit } from "react-icons/lu";
import Breadcrumb from "components/molecules/Breadcrumb";
import { MdDelete } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { deleteData, getData } from "actions";
import { API_URL_helpTopic } from "constants/api";
import { helpTopicReducers } from "redux/helpTopicSlice";
import useDebounce from "hooks/useDebounce";
import { BiSortDown, BiSortUp } from "react-icons/bi";
import { FaEdit, FaTrash } from "react-icons/fa";
import moment from "moment";
import { SyncLoader } from "react-spinners";
import { AuthContext } from "context/AuthContext";

// field for use sort up/down
const TABLE_HEAD = [
  { title: "Topic", field: "topic" },
  { title: "Status", field: "status" },
  { title: "Type", field: "type" },
  { title: "Priority", field: "priority" },
  { title: "department", field: "department" },
  { title: "Last updated", field: "updated_at" },
  { title: "Actions", field: "" },
];

const Helptopics = () => {
  const navigate = useNavigate();
  const [adminAccess, setAdminAccess] = useState(false);
  const { datauser } = useContext(AuthContext);
  const dispatch = useDispatch();
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState("");
  const [sortColumn, setSortColumn] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [currentPage, setCurrentPage] = useState(0);

  const {
    getHelpTopicResult,
    getHelpTopicLoading,
    getHelpTopicError,
    addHelpTopicResult,
    deleteHelpTopicResult,
  } = useSelector((state) => state.helpTopic);

  const get = useCallback(
    async (params) => {
      getData(
        API_URL_helpTopic,
        params,
        { dispatch, redux: helpTopicReducers },
        "GET_HELPTOPIC"
      );
    },
    [dispatch]
  );

  const onDelete = (item) => {
    deleteData(
      API_URL_helpTopic + item.id,
      { dispatch, redux: helpTopicReducers },
      "DELETE_HELPTOPIC"
    );
  };

  const onEdit = (item) => {
    navigate(`/administrator/manage/helptopics/edit`, {
      state: {
        item,
      },
    });
  };

  const action = [
    {
      name: "Edit",
      icon: <FaEdit />,
      func: onEdit,
      colorlight: "bg-sky-500",
      colordark: "dark:bg-sky-800",
      hoverlight: "hover:bg-sky-600",
      hoverdark: "hover:dark:bg-sky-900",
    },
    {
      name: "Delete",
      icon: <FaTrash />,
      func: onDelete,
      colorlight: "bg-red-500",
      colordark: "dark:bg-red-800",
      hoverlight: "hover:bg-red-600",
      hoverdark: "hover:dark:bg-red-900",
    },
  ];

  const fetchData = useCallback(() => {
    const params = `?all=True&limit=${limit}&offset=${offset}&search=${search}&ordering=${
      sortOrder === "desc" ? "-" : ""
    }${sortColumn}`;
    get(params);
  }, [get, limit, offset, sortColumn, sortOrder]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (addHelpTopicResult) {
      fetchData();
    }
  }, [addHelpTopicResult, fetchData]);

  useEffect(() => {
    if (deleteHelpTopicResult) {
      fetchData();
    }
  }, [deleteHelpTopicResult, fetchData]);

  useEffect(() => {
    if (datauser !== "") {
      const permissionViewMenuAdmin = datauser.group_permissions.includes(
        "access_menu_admin"
      );
      if (permissionViewMenuAdmin) {
        setAdminAccess(true);
      } else {
        navigate("/dashboard/agent");
      }
    }
  }, [datauser]);

  if (!adminAccess) {
    return null;
  }

  return (
    <Container>
      <Breadcrumb
        title={"Manage"}
        excludedPaths={["manage", "administrator"]}
        group={"Superadmin"}
      />
      <div className="bg-neutral dark:bg-neutral-800 rounded overflow-hidden shadow">
        <div className="h-12 w-full grid items-center px-4 bg-neutral-100 dark:bg-neutral-700 border-b border-neutral-200 dark:border-neutral-600 text-xs md:text-base">
          Help topic
          <div className="col-end-7 flex justify-end">
            <Link
              to="/administrator/manage/helptopics/create"
              className="flex items-center gap-1 p-1 px-3 rounded shadow-sm border bg-neutral-100 hover:bg-neutral-200 dark:bg-neutral-700 dark:border-neutral-600 dark:hover:bg-neutral-800"
            >
              <AiOutlinePlus />
              Create help topic
            </Link>
          </div>
        </div>

        <div className="flex flex-col md:flex-row text-center md:justify-between align-middle px-4 my-4 bg-neutral dark:bg-neutral-800 "></div>

        {/* <CardContainer title="List of agents"> */}
        <div className="mx-4">
          <div className="overflow-x-auto min-h-[55vh]">
            <table className="border border-collapse items-center w-full">
              <thead className="text-left">
                <tr>
                  {TABLE_HEAD.map((item, itemIdx) => (
                    <th
                      key={itemIdx}
                      className="p-2 border text-sm whitespace-nowrap cursor-pointer"
                    >
                      <span className="flex">{item.title}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* loading  */}
                {getHelpTopicLoading && (
                  <tr>
                    <td
                      className="text-center py-12"
                      colSpan={TABLE_HEAD.length + 1}
                    >
                      <div className="pt-10 pb-6 flex justify-center items-center">
                        <SyncLoader color="#4477CE" />
                      </div>
                    </td>
                  </tr>
                )}
                {/* Error */}
                {getHelpTopicError && (
                  <tr>
                    <td className="text-center" colSpan={TABLE_HEAD.length + 1}>
                      <div className="pt-20 pb-12 flex justify-center items-center text-xs text-red-500">
                        {getHelpTopicError}
                      </div>
                    </td>
                  </tr>
                )}
                {/* Result = 0 */}
                {getHelpTopicResult.results &&
                  getHelpTopicResult.results.length === 0 && (
                    <tr>
                      <td
                        className="text-center"
                        colSpan={TABLE_HEAD.length + 1}
                      >
                        <div className="pt-20 pb-12 flex justify-center items-center text-xs text-slate-600 dark:text-white">
                          No Data
                        </div>
                      </td>
                    </tr>
                  )}

                {getHelpTopicResult.results &&
                  getHelpTopicResult.results.map((item, index) => (
                    <tr key={index}>
                      <td className="p-4 border border-blue-gray-50">
                        <button
                          className="text-blue-700"
                          onClick={() => onEdit(item)}
                        >
                          {item.topic}{" "}
                        </button>
                      </td>
                      <td className="p-4 border border-blue-gray-50">
                        <p
                          className={`${
                            item.status ? "text-green-500" : "text-red-600"
                          } `}
                        >
                          {item.status ? "Active" : "Disable"}
                        </p>
                      </td>
                      <td className="p-4 border border-blue-gray-50">
                        <p
                          className={`${
                            item.type ? "text-green-500" : "text-red-600"
                          } `}
                        >
                          {item.type ? "Public" : "Private"}
                        </p>
                      </td>
                      <td className="p-4 border border-blue-gray-50">
                        {item.priority_name}
                      </td>
                      <td className="p-4 border border-blue-gray-50">
                        {item.department_name}
                      </td>
                      <td className="p-4 border border-blue-gray-50">
                        {moment(item.updated_at).format("DD/MM/YYYY HH:mm:ss")}
                      </td>
                      <td className="p-4 border border-blue-gray-50">
                        <div className="flex">
                          {action.map((action, actionIdx) => (
                            <button
                              key={actionIdx}
                              className={`mx-0.5 text-white text-sm ${action.colorlight} ${action.colordark} ${action.hoverlight} ${action.hoverdark} rounded p-1 `}
                              onClick={() => action.func(item)}
                            >
                              <div className="flex items-center">
                                {action.icon} {action.name}
                              </div>
                            </button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          <div className="pt-5 py-11"></div>
        </div>
        {/* </CardContainer> */}
      </div>
    </Container>
  );
};

export default Helptopics;
